import {
  convertPlainObjectFieldsToFirestoreTypes,
  getMinutesInBatch,
  PlaytimeBatchInfo,
  TaskType,
} from '@playback-rewards/shared-libraries'
import _ from 'lodash'

import {
  checkIfPlaytimeTaskCompleted,
  getActiveIndex,
  isGenericTaskHidden,
  isGenericTaskNotStarted,
  isLockedGenericTask,
  isPurchaseTaskLocked,
} from 'utils/genericOffer'
import { determineActiveTask, isActivatedOfferIsExpired } from 'utils/offerCard'
import { getActiveStreakProgress } from 'utils/streak'
import { OFFER_TYPE } from 'constants/index'

/**
 * Whether an entry of featuredRow is the Refer a Friend offer rather than a game.
 *
 * The backend adds the referral document to the row as-is. It carries isFeatured, which game
 * offers never do (PbrOfferFE does not pick it), and none of the game fields: no gameName,
 * iconUrl, unifiedAppId or isUAOffer.
 */
export const isReferralOffer = (offer) => Boolean(offer?.isFeatured)

/** A piggy bank is worth one cent, the same rate the backend prices value statements at. */
const PIGGY_BANKS_PER_DOLLAR = 100

/**
 * Mirrors the backend's isTaskConfigVisibleBeforeActivation: a task the offer advertises
 * up front, before the user activates it.
 */
const isVisibleBeforeActivation = (task) => !task.hideBeforeActive || !(task.delayToUnlockMins > 0)

/**
 * The milestones an offer advertises before activation: one per playtime batch, then the
 * generic tasks that are not hidden until the offer is running.
 *
 * Playtime lives in batchPlaytimeConfig rather than in tasks, the same split the offer
 * detail page renders, so a PLAYTIME entry in tasks would double count the batches.
 */
export const getOfferMilestones = (offer) => {
  const batchConfig = offer?.batchPlaytimeConfig
  const minutesPerBatch = batchConfig ? getMinutesInBatch(batchConfig) : 0

  const playtimeMilestones = Array.from({ length: batchConfig?.batchCount || 0 }, () => ({
    piggyBanks: batchConfig.pigsPerBatch || 0,
    minutes: minutesPerBatch,
  }))

  const taskMilestones = (offer?.tasks || [])
    .filter((task) => task.taskType !== TaskType.PLAYTIME && isVisibleBeforeActivation(task))
    .map((task) => ({ piggyBanks: task.rewards?.piggyBanks || 0, minutes: 0 }))

  return [...playtimeMilestones, ...taskMilestones]
}

export const getOfferTotalPiggyBanks = (offer) =>
  getOfferMilestones(offer).reduce((total, milestone) => total + milestone.piggyBanks, 0)

export const piggyBanksToDollars = (piggyBanks) => piggyBanks / PIGGY_BANKS_PER_DOLLAR

/**
 * Resolves the playtime batches of a generic offer: how many there are, which one is running
 * and whether it is done. Returns null for an offer with no batches.
 */
const getPlaytimeBatches = (activatedOffer) => {
  const batchConfig = activatedOffer.batchPlaytimeConfig
  if (!batchConfig?.batchCount) {
    return null
  }

  // PlaytimeBatchInfo reads firestore timestamps, which the api sends as plain objects.
  const offerCopy = _.cloneDeep(activatedOffer)
  convertPlainObjectFieldsToFirestoreTypes(offerCopy)
  const batchInfo = new PlaytimeBatchInfo(offerCopy)
  const batches = Array.from({ length: batchConfig.batchCount })
  const activeIndex = getActiveIndex(batches, batchInfo)

  return {
    batchConfig,
    batchInfo,
    count: batchConfig.batchCount,
    activeIndex,
    isActiveCompleted:
      activeIndex > -1 && checkIfPlaytimeTaskCompleted(batchConfig, batchInfo, activeIndex),
  }
}

/**
 * The task a user is working on right now, as a plain descriptor.
 *
 * Generic offers keep their playtime in batches and everything else in tasks, so those are
 * resolved here the way the offer card footer resolves them for rendering. Every other offer
 * type goes through determineActiveTask, the selector the offer card already uses.
 */
export const getCurrentTask = (activatedOffer, { hasParsingEnabled, timer } = {}) => {
  if (!activatedOffer || isActivatedOfferIsExpired(activatedOffer)) {
    return null
  }

  if (activatedOffer.offerType === OFFER_TYPE.GENERIC_OFFER) {
    const playtime = getPlaytimeBatches(activatedOffer)

    if (playtime && playtime.activeIndex > -1 && !playtime.isActiveCompleted) {
      return {
        piggyBanks: playtime.batchConfig.pigsPerBatch || 0,
        minutesLeft: Math.ceil(
          playtime.batchInfo.getRemainingMinutesInBatch(playtime.activeIndex) || 0
        ),
      }
    }

    const activeGenericTask = activatedOffer.tasks?.find(
      (task) =>
        task.taskType !== TaskType.PLAYTIME &&
        !task.completed &&
        !isGenericTaskNotStarted(task) &&
        !isGenericTaskHidden(task, true) &&
        !isLockedGenericTask(task, activatedOffer.tasks) &&
        !isPurchaseTaskLocked(task, hasParsingEnabled)
    )

    if (activeGenericTask) {
      return { piggyBanks: activeGenericTask.rewards?.piggyBanks || 0, minutesLeft: null }
    }
  }

  const activeTask = determineActiveTask(activatedOffer, timer, hasParsingEnabled)

  return activeTask ? { piggyBanks: activeTask.piggies || 0, minutesLeft: null } : null
}

/**
 * How far through the whole offer the user is, counted in milestones rather than in rewards
 * so every milestone weighs the same: the playtime batches already finished plus the tasks
 * marked completed, over the offer's milestones.
 *
 * Returns null for an offer that advertises no milestones, which leaves the ring empty
 * rather than guessing a fraction.
 */
export const getOfferProgress = (activatedOffer) => {
  if (!activatedOffer) {
    return null
  }

  const playtime = getPlaytimeBatches(activatedOffer)
  // Counted by minutes played, not by elapsed batch windows.
  const completedBatches = playtime
    ? Array.from({ length: playtime.count }).filter((_, batchIndex) =>
        checkIfPlaytimeTaskCompleted(playtime.batchConfig, playtime.batchInfo, batchIndex)
      ).length
    : 0

  const tasks = (activatedOffer.tasks || []).filter((task) => task.taskType !== TaskType.PLAYTIME)
  const total = (playtime?.count || 0) + tasks.length

  if (!total) {
    return null
  }

  const completed = completedBatches + tasks.filter((task) => task.completed).length

  return { completed, total, progress: Math.min(completed / total, 1) }
}

/** The piggy banks waiting across every offer the user has in progress. */
export const getPendingPiggyBanks = (offers = [], options) =>
  offers.reduce(
    (total, offer) => total + (getCurrentTask(offer.activatedOffer, options)?.piggyBanks || 0),
    0
  )

/**
 * How many days in a row the offer has been kept alive, or null when it has no streak task.
 *
 * This is only what the flame shows; how far through the offer the user is lives in
 * getOfferProgress.
 */
export const getOfferStreak = (activatedOffer) => {
  const streakTasks = (activatedOffer?.tasks || []).filter(
    (task) => task.taskType === TaskType.STREAK
  )

  if (!streakTasks.length) {
    return null
  }

  const longestTask = streakTasks.reduce((longest, task) =>
    task.streakRule.test.count > longest.streakRule.test.count ? task : longest
  )

  return {
    days: getActiveStreakProgress(longestTask.streak?.events, longestTask.streak?.userTimezone),
  }
}
